import { Children } from 'react';
import { Avatar as BaseAvatar } from '@base-ui/react/avatar';
import { RiUser6Line } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { chipBackground, colorFromString, type ChipColor } from './lib/colors';

export type AvatarSize = 16 | 20 | 24 | 32 | 36 | 44 | 48 | 64 | 96 | 128;

const BOX: Record<AvatarSize, string> = {
  16: 'size-16',
  20: 'size-20',
  24: 'size-24',
  32: 'size-32',
  36: 'size-36',
  44: 'size-44',
  48: 'size-48',
  64: 'size-64',
  96: 'size-96',
  128: 'size-128',
};
/** Initial font per size — kit 67:11563 (600) with product overrides: table 20 → 9/auto, panel header 36 → 14/auto. */
const INITIALS: Record<AvatarSize, string> = {
  16: 'text-[0.5rem] leading-none',
  20: 'text-9 leading-none',
  24: 'text-xxs leading-none',
  32: 'text-xs leading-none',
  36: 'text-sm leading-none',
  44: 'text-sm leading-none',
  48: 'text-sm leading-none',
  64: 'text-xl leading-none',
  96: 'text-3xl leading-none',
  128: 'text-4xl leading-none',
};
const PLACEHOLDER_ICON: Record<AvatarSize, string> = {
  16: 'size-10',
  20: 'size-12',
  24: 'size-14',
  32: 'size-16',
  36: 'size-16',
  44: 'size-16',
  48: 'size-24',
  64: 'size-32',
  96: 'size-48',
  128: 'size-64',
};
/** Status dot size / ring (kit): 4/1 (≤24) · 6/1 (32–36) · 8/2 (44–48) · 10/2 (64) · 12/4 (96–128). */
const DOT: Record<AvatarSize, string> = {
  16: 'size-4 ring-1',
  20: 'size-4 ring-1',
  24: 'size-4 ring-1',
  32: 'size-6 ring-1',
  36: 'size-6 ring-1',
  44: 'size-8 ring-2',
  48: 'size-8 ring-2',
  64: 'size-10 ring-2',
  96: 'size-12 ring-4',
  128: 'size-12 ring-4',
};

export interface AvatarProps {
  size?: AvatarSize;
  /** Photo URL; falls back to initials / placeholder while loading or on error. */
  src?: string | null;
  /** Person name — used for initials and `alt`. */
  name?: string | null;
  /** Override the generated initials. */
  initials?: string;
  /**
   * Solid initial avatar in a chip colour (product tables 497:21534). `"auto"` picks a stable colour from the name.
   * Without it, the kit "Name" style is used: accent/20 + accent initials.
   */
  color?: ChipColor | 'auto';
  /** Presence dot: `online` (#1ea324) or `accent`. */
  status?: 'online' | 'accent' | false;
  /** Thin White/40 outline (viewer user menu 14:7285). */
  ring?: boolean;
  className?: string;
}

export function getInitials(name: string | null | undefined, letters = 1): string {
  if (!name) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, letters)
    .map((w) => w[0]!.toLocaleUpperCase('uz'))
    .join('');
}

export function Avatar({ size = 32, src, name, initials, color, status, ring, className }: AvatarProps) {
  const text = initials ?? getInitials(name, size >= 48 ? 2 : 1);
  const resolvedColor = color === 'auto' ? colorFromString(name ?? text) : color;
  const solid = resolvedColor ? chipBackground(resolvedColor) : undefined;
  return (
    <BaseAvatar.Root
      className={cx(
        'relative inline-flex shrink-0 items-center justify-center rounded-full align-middle select-none',
        BOX[size],
        ring && 'ring-1 ring-white-400',
        className,
      )}
    >
      <span
        className={cn(
          'flex size-full items-center justify-center overflow-hidden rounded-full font-semibold',
          solid ? cn(solid.className, 'text-white') : text ? 'bg-accent-soft text-accent' : 'bg-field text-icon',
        )}
        style={solid?.style}
      >
        {src && <BaseAvatar.Image src={src} alt="" className="size-full object-cover" />}
        <BaseAvatar.Fallback className={cn('flex size-full items-center justify-center', INITIALS[size])}>
          {text ? (
            <span aria-hidden={name ? true : undefined}>{text}</span>
          ) : (
            <RiUser6Line aria-hidden className={PLACEHOLDER_ICON[size]} />
          )}
        </BaseAvatar.Fallback>
      </span>
      {name && <span className="sr-only">{name}</span>}
      {status && (
        <span
          aria-hidden
          className={cn(
            'absolute right-0 bottom-0 rounded-full ring-panel',
            DOT[size],
            status === 'online' ? 'bg-other-1ea324' : 'bg-accent',
            // viewer user menu (14:7285): 12 px online dot with a 1px white ring, overlapping the bottom edge
            ring && size === 44 && 'right-6 -bottom-2 size-12 ring-1 ring-white',
          )}
        />
      )}
    </BaseAvatar.Root>
  );
}

const OVERLAP: Partial<Record<AvatarSize, string>> = {
  16: '-ml-4',
  20: '-ml-4',
  24: '-ml-6',
  32: '-ml-8',
  36: '-ml-8',
  48: '-ml-12',
};

export interface AvatarGroupProps {
  size?: 16 | 20 | 24 | 32 | 36 | 48;
  /** Show at most `max` avatars, then a "+N" counter (kit `#3576ec`). */
  max?: number;
  /** Total count when more people exist than `children` (server-side lists). */
  total?: number;
  children: React.ReactNode;
  className?: string;
}

/** AvatarGroup — kit 67:11527: overlap −4…−12, ring 2px (≤24) / 4px (≥32) in the surface colour. */
export function AvatarGroup({ size = 24, max, total, children, className }: AvatarGroupProps) {
  const items = Children.toArray(children);
  const visible = max != null ? items.slice(0, max) : items;
  const rest = (total ?? items.length) - visible.length;
  const ringCls = size <= 24 ? 'ring-2' : 'ring-4';
  return (
    <div className={cn('flex items-center', className)}>
      {visible.map((child, i) => (
        <span key={i} className={cn('inline-flex rounded-full ring-panel', ringCls, i > 0 && OVERLAP[size])}>
          {child}
        </span>
      ))}
      {rest > 0 && (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-control font-semibold text-white ring-panel',
            BOX[size],
            INITIALS[size],
            ringCls,
            visible.length > 0 && OVERLAP[size],
          )}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}
